import { Pencil, Send, UserCheck, UserX } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { RowAction } from '@nucleus-commerce/admin-kit/index-table';
import type { PlatformUser } from '@nucleus-commerce/api-client/platform';

import { canToggleStatus, editRestrictions, type Actor } from '../schemas/user-rules';

export type UserRowAction = 'edit' | 'resend' | 'disable' | 'enable';

export interface UserRowActionsOptions {
  /** Null for roles that only read; there are then no row actions. */
  actor: Actor | null;
  users: readonly PlatformUser[];
  onAction: (action: UserRowAction, user: PlatformUser) => void;
}

/** Per-row menu. Entries the backend would refuse (self, last owner, owner as non-owner) are hidden. */
export function useUserRowActions({ actor, users, onAction }: UserRowActionsOptions) {
  const { t } = useTranslation('users');

  return useMemo<Array<RowAction<PlatformUser>> | undefined>(() => {
    if (!actor) return undefined;
    const canEdit = (user: PlatformUser) => editRestrictions(actor, user, users).canEdit;
    const canToggle = (user: PlatformUser) => canEdit(user) && canToggleStatus(actor, user, users);
    return [
      {
        id: 'edit',
        label: t('actions.edit'),
        icon: <Pencil aria-hidden />,
        hidden: (user) => !canEdit(user),
        onAction: (user) => onAction('edit', user),
      },
      {
        id: 'resend',
        label: t('actions.resend'),
        icon: <Send aria-hidden />,
        hidden: (user) => !canEdit(user) || user.hasPassword || user.status !== 'active',
        onAction: (user) => onAction('resend', user),
      },
      {
        id: 'disable',
        label: t('actions.disable'),
        icon: <UserX aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: (user) => !canToggle(user) || user.status !== 'active',
        onAction: (user) => onAction('disable', user),
      },
      {
        id: 'enable',
        label: t('actions.enable'),
        icon: <UserCheck aria-hidden />,
        separatorBefore: true,
        hidden: (user) => !canToggle(user) || user.status === 'active',
        onAction: (user) => onAction('enable', user),
      },
    ];
  }, [actor, onAction, t, users]);
}
